import React from 'react';

import Tabs from './tabs';
import DetailPanelLeft from './detail_panel_left';
import DetailPanelRight from './detail_panel_right';
import TimeRateControls from './time_rate_controls';
import SCZoomButton from './sc_zoom_button';
import Select from './select';
import DatePicker from './date_picker';
import CloseButton from './close_button';
import ar from '../languages/ar';

import { compareStore, refsStore, spacecraftStore, videoStore, layersStore } from '../managers/globalState';

import '../../www/assets/css/detail_overlay.css';
import CalendarAltSolidSVG from '../../www/assets/images/calendar_alt_solid.svg';

class MobileDetailOverlay extends React.PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			open: false,
			blueMarble: false,
			view: '',
			vitalsign: '',
			activeIndex: 0,
			button: null,
			title: '',
			subtitle: '',
			isZoomedOut: false
		};

		const { get } = refsStore.stateSnapshot;
		this.compareList = get('comparison').getCompareList();
		this.videoManager = get('video');

		this._goToVitalView = this._goToVitalView.bind(this);
		this._changeAltView = this._changeAltView.bind(this);
		this._renderContent = this._renderContent.bind(this);
		this._resetToLatest = this._resetToLatest.bind(this);
		this._resetToLatestSeven = this._resetToLatestSeven.bind(this);
		this.handleNestedSelection = this.handleNestedSelection.bind(this);
		this.handleMobileCompare = this.handleMobileCompare.bind(this);
		this.toggle = this.toggle.bind(this);

		this.contentManager = get('content');
		this.sceneManager = get('scene');
	}

	static getDerivedStateFromProps(nextProps, state) {
		const {
			view, vitalsign, missionName, altid, altView, event
		} = nextProps;

		const { videoId } = videoStore.stateSnapshot;
		const { get } = refsStore.stateSnapshot;
		let { title, subtitle } = get('content').getDisplayData({
			view,
			missionName,
			vitalsign,
			altid,
			altView,
			event
		});

		const videoType = get('video').getVideoType(videoId);
		if (videoType === 'global' || videoType === 'geoLocated') {
			const videoData = get('video').getVideoLeftPanelContent(videoId);
			title = videoData.title;
			subtitle = videoData.subtitle;
		}

		if (view !== state.view || vitalsign !== state.vitalsign) {
			if (view === 'event') {
				return {
					view,
					title
				};
			} else {
				let button = null;
				let showCloudFree = false;
				if (view === 'vitalsign') {
					if (vitalsign === 'visible_earth') {
						button = {
							value: 'satellites',
							text: ar.satellites_now_link,
							id: 'satellites-button'
						};
					} else if (vitalsign === 'satellites') {
						showCloudFree = true;
						button = {
							value: 'visible_earth',
							text: ar.visible_earth,
							id: 'visible-earth-button'
						};
					}
				}
				return {
					view,
					showCloudFree,
					activeIndex: 0,
					button,
					title,
					subtitle
				};
			}
		}

		return null;
	}

	componentDidMount() {
		// Always close the overlay when isZoomedOut state changes.
		this.scUnsubscribe = spacecraftStore.subscribeTo('isZoomedOut', zoom => this.setState({ open: false, isZoomedOut: zoom }));
		this.bmUnsubscribe = layersStore.subscribeTo('blueMarble', blueMarble => this.setState({ blueMarble }));
	}

	componentWillUnmount() {
		typeof this.scUnsubscribe === 'function' && this.scUnsubscribe();
		typeof this.bmUnsubscribe === 'function' && this.bmUnsubscribe();
	}

	toggle() {
		window.scroll(0, 0);
		this.setState({ open: !this.state.open });
	}

	handleNestedSelection(event) {
		const { detailPanelProps } = this.props;
		detailPanelProps.handleSelection(event);
		this.toggle();
	}

	handleMobileCompare(compareObjIndex) {
		const { get } = refsStore.stateSnapshot;
		const compareObjId = this.compareList[compareObjIndex];
		get('comparison').updateCompareObject(compareObjId.text);
		this.toggle();
	}

	_goToVitalView(event) {
		this.props.goToVitalView(event);
		this.toggle();
	}

	_changeAltView(dataset, text) {
		const { get } = refsStore.stateSnapshot;
		const routeManager = get('route');

		this.toggle();

		const videoId = this.videoManager.getVideoIdFromTitle(text);
		if (videoId) {
			// Handle video cases
			videoStore.setGlobalState({ videoOpenedFrom: 'mobileDetailsOverlay' });

			routeManager.navigate({
				animating: 'f',
				start: '',
				end: '',
				videoId,
				__remove: ['spacecraft']

			});
		} else {
			// Handle Alt Dataset cases.
			this.props.changeAltView(dataset);
		}
	}

	_resetToLatest() {
		const { get } = refsStore.stateSnapshot;

		this.videoManager.setVideoQuery(null);
		this.toggle();

		get('dataset').resetToLatest();
	}

	_resetToLatestSeven() {
		const { get } = refsStore.stateSnapshot;

		this.videoManager.setVideoQuery(null);
		this.toggle();

		get('dataset').playLatestSeven();
	}

	_renderContent(type) {
		const {
			button, subtitle, isZoomedOut, blueMarble, showCloudFree
		} = this.state;

		const {
			isMobile, view, vitalsign, altView, spacecraft,
			detailPanelProps, datePickerProps
		} = this.props;
		const { get } = refsStore.stateSnapshot;
		const { datasetGroup, isVisibleEarth } = get('dataset') || {};
		const { instrument } = get('content').getEntityInfo(spacecraft) || {};
		const { compareObject } = compareStore.stateSnapshot;

		// We should handle the dynamic subtitle generation when the subtitle is created in contentManager.getDisplayData()
		// Rather than resetting it here.
		let dynamicSubtitle = get('content').getDynamicData()?.title;

		// Handle the dyanamicSubtitle case when we are using video data to populate the leftContent.
		const { videoId } = videoStore.stateSnapshot;
		const videoType = this.videoManager.getVideoType(videoId);
		if (videoType === 'global' || videoType === 'geoLocated') { dynamicSubtitle = null; }

		if (isZoomedOut || instrument || !spacecraft) {
			// If zoomed out, instrument view or not in spacecraft view, reset comparison object
			get('comparison').reset(spacecraft, isZoomedOut);
		}

		const showCompareDropdown = !isZoomedOut && spacecraft && !instrument;
		const allDropDownEntries = [];
		if (!spacecraft && datasetGroup) {
			const { vitalData } = this.contentManager;
			const { datasets: allDatasets } = datasetGroup;

			// Add dataset ids for this vital sign to the dropDownEntries array
			allDropDownEntries.push(...allDatasets);
			allDropDownEntries.push(...this.videoManager.getVideoDropdownEntries(vitalData.value));
		}

		// Set default value of compare select based on selected item in dropdown
		let compareDefaultValue = this.compareList.length - 1;
		if (showCompareDropdown) {
			for (let i = 0; i < this.compareList.length; i++) {
				const selectedCompareId = get('content').getCustomEntityId(this.compareList[i].text);
				if (selectedCompareId === compareObject) {
					compareDefaultValue = this.compareList[i].id;
				}
			}
		}

		if (type === 'options') {
			const relatedDatasets = get('content').getRelatedDatasets(spacecraft);
			const { isLatest, isLatestSeven } = get('dataset');

			return (
				<div className='content-container'>
					{
						view !== 'spacecraft' && subtitle
						&& <div className='tab-subtitle'>{dynamicSubtitle}</div>
					}
					{
						((view === 'spacecraft' || vitalsign === 'satellites')
						&& (videoType !== 'global' && videoType !== 'geoLocated'))
						&& (
							<div className='section'>
								<TimeRateControls
									{...this.props}
									open
								/>
							</div>
						)
					}
					{
						showCloudFree
						&& (
							<div className='section'>
								<div
									className='mobile-zoom-button' onClick={() => {
										const { blueMarble } = layersStore.stateSnapshot;
										this.sceneManager.toggleTexture(!blueMarble ? 'bluemarble' : 'wmts');
									}}
								>
									<span>{ar.Cloud_free_Earth}</span>
									<input className='checkbox' type='checkbox' checked={blueMarble} readOnly />
									<span className='pseudo-checkbox' />
								</div>
							</div>
						)
					}
					{
						view === 'spacecraft'
						&& (
							<>
								<div className='section'>
									<SCZoomButton isMobile={isMobile} />
								</div>
								{
									showCompareDropdown
									&& (
										<div className='section'>
											<Select
												mode='toggle'
												title={ar.compare_size}
												defaultValue={compareDefaultValue}
												onChange={this.handleMobileCompare}
												options={this.compareList.map((compareObj, index) => ({
													value: index,
													text: compareObj.text
												}))}
											/>
										</div>
									)
								}

								{
									relatedDatasets.length !== 0
									&& (
										<div className='section datasets'>
											<DetailPanelRight
												{...detailPanelProps}
												handleSelection={this.handleNestedSelection}
											/>
										</div>
									)
								}
							</>
						)
					}
					{
						view === 'vitalsign' && vitalsign !== 'satellites'
						&& (videoType !== 'global' && videoType !== 'geoLocated')
						&& (
							<>
								<div className={'section' + (isLatest ? ' selected' : '')} onClick={this._resetToLatest}>
									{ar.latest}
									{' '}
									{ar.dataset}
								</div>
								<div className={'section' + (isLatestSeven ? ' selected' : '')} onClick={this._resetToLatestSeven}>
									{ar.latest_seven}
									{' '}
									{ar.datasets}
								</div>
								<div className='section'>
									<Select
										mode='toggle'
										title={ar.choose_dates}
										className='no-arrow'
										arrowAfter
										icon={<CalendarAltSolidSVG />}
										element={
											<DatePicker
												{...datePickerProps}
												stayVisible
												onChange={this.toggle}
											/>
										}
									/>
								</div>
							</>
						)
					}
					{
						button
						&& (
							<div className='section'>
								<button data-value={button.value} id={button.id} className='action-button' onClick={this._goToVitalView}>{button.text}</button>
							</div>
						)
					}
					{
						(view === 'vitalsign' && allDropDownEntries.length > 1)
						&& (
							<div className='section'>
								<Select
									mode='toggle'
									title={ar.more_datasets}
									className='no-arrow'
									arrowAfter
									defaultValue={altView}
									onChange={this._changeAltView}
									options={allDropDownEntries.map((menuItem, index) => ({
										value: index,
										text: menuItem.menuTitle,
										category: menuItem.category
									}))}
								/>
							</div>
						)
					}
				</div>
			);
		} else {
			return (
				<DetailPanelLeft
					open
					hideTitle
					hideButton
					{...detailPanelProps}
					toggle={this.toggle}
					toggleOverlay={this.toggle}
				/>
			);
		}
	}

	render() {
		const {
			open, activeIndex, title
		} = this.state;
		const { view } = this.props;
		if (!open) {
			return null;
		}

		return (
			<div className='overlay detail-overlay scrollable'>
				<div className='header'>
					<div className='title'>{title}</div>
					<CloseButton responsive onClick={this.toggle} />
				</div>
				<Tabs activeIndex={activeIndex}>
					<div label={ar.info1}>
						{this._renderContent('info')}
					</div>
					{
						view !== 'event'
						&& (
							<div label={ar.options}>
								{this._renderContent('options')}
							</div>
						)
					}
				</Tabs>
			</div>
		);
	}
}

export default MobileDetailOverlay;
